import React, { useState } from "react"
import * as yup from "yup"
import { Box, Button } from "src/UILibrary"
import { useTranslation } from "react-i18next"
import { useMediaQuery } from "react-responsive"
import { validateField } from "src/modules/validation"
import InputTextField from "src/components/atom/InputTextField"
import { AuthenticationService } from "src/openapi"
import { useLoginContext } from "src/hooks/useLoginContext"
import { useRecoilState, useRecoilState_TRANSITION_SUPPORT_UNSTABLE } from "recoil"
import { ShowToast } from "src/states/showToast"
import { RESPONSE_CODE, SNACKBAR_TYPES } from "src/modules/const"
import { ShowProgress } from "src/states/showProgress"

export const ChangePasswordPageItem = () => {
  const { t } = useTranslation("translation", {})
  const [, setShowToast] = useRecoilState_TRANSITION_SUPPORT_UNSTABLE(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const isTablet = useMediaQuery({ query: "(min-width: 1200px)" })

  const [oldpassword, setOldPassword] = useState<string>("")
  const [password, setPassword] = useState<string>("")
  const [passwordConfirm, setPasswordConfirm] = useState<string>("")

  const [oldpasswordValidationMessage, setOldPasswordValidationMessage] = useState<string>("")
  const [passwordValidationMessage, setPasswordValidationMessage] = useState<string>("")
  const [passwordConfirmValidationMessage, setPasswordConfirmValidationMessage] =
    useState<string>("")

  const { accessToken, checkLoginStatus } = useLoginContext()

  const validate = async () => {
    let ret = true

    try {
      await validateField(
        yup.object().shape({
          oldpassword: yup
            .string()
            .required(t("signup.please_input_password"))
            .min(8, t("signup.password_length_min"))
            .max(20, t("signup.password_length_max"))
            .test("isValidPassword", t("signup.password_regex"), (value) => {
              const hasLowerCase = /[a-z]/.test(value)
              const hasNumber = /[0-9]/.test(value)

              return hasLowerCase && hasNumber
            }),
        }),
        "oldpassword",
        oldpassword
      )
      setOldPasswordValidationMessage("")
    } catch (err: any) {
      setOldPasswordValidationMessage(err.message)
      ret = ret && false
    }

    if (password != passwordConfirm) {
      setPasswordValidationMessage(t("password_reset.password_password_confirm_different"))
      ret = ret && false
    } else {
      try {
        await validateField(
          yup.object().shape({
            password: yup
              .string()
              .required(t("signup.please_input_password"))
              .min(8, t("signup.password_length_min"))
              .max(20, t("signup.password_length_max"))
              .test("isValidPassword", t("signup.password_regex"), (value) => {
                const hasLowerCase = /[a-z]/.test(value)
                const hasNumber = /[0-9]/.test(value)

                return hasLowerCase && hasNumber
              }),
          }),
          "password",
          password
        )
        setPasswordValidationMessage("")
      } catch (err: any) {
        setPasswordValidationMessage(err.message)
        ret = ret && false
      }

      try {
        await validateField(
          yup.object().shape({
            passwordConfirm: yup
              .string()
              .required(t("signup.please_input_password"))
              .min(8, t("signup.password_length_min"))
              .max(20, t("signup.password_length_max"))
              .test("isValidPassword", t("signup.password_regex"), (value) => {
                const hasLowerCase = /[a-z]/.test(value)
                const hasNumber = /[0-9]/.test(value)

                return hasLowerCase && hasNumber
              }),
          }),
          "passwordConfirm",
          passwordConfirm
        )
        setPasswordConfirmValidationMessage("")
      } catch (err: any) {
        setPasswordConfirmValidationMessage(err.message)
        ret = ret && false
      }
    }

    return ret
  }

  const handleSave = async () => {
    if (!(await validate())) {
      return
    }

    setShowProgress(true)
    AuthenticationService.changePassword(accessToken, {
      oldPassword: oldpassword,
      newPassword: password,
    })
      .then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.SUCCESS) {
          setShowToast({
            show: true,
            text: t("mypage.profile.success"),
            type: SNACKBAR_TYPES.SUCCESS,
          })
        } else {
          setShowToast({ show: true, text: t("mypage.profile.failed"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowToast({ show: true, text: t("mypage.profile.failed"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  return (
    <Box
      sx={{
        ...(isTablet
          ? { height: "800px", pt: "20px", pl: "24px" }
          : { width: "100%", pl: "10px", position: "absolute", top: 60, bottom: 120 }),
        overflowY: "scroll",
      }}
    >
      <InputTextField
        name={t("mypage.profile.current_password")}
        password
        required
        text={oldpassword}
        handleText={setOldPassword}
        sx={{ mt: "40px", mr: "10px" }}
        placeholder={t("mypage.profile.old_password_placeholder")}
        validationMessage={oldpasswordValidationMessage}
      />
      <InputTextField
        name={t("mypage.profile.new_password")}
        password
        required
        text={password}
        handleText={setPassword}
        sx={{ mt: "40px", mr: "10px" }}
        placeholder={t("mypage.profile.new_password_placeholder")}
        validationMessage={passwordValidationMessage}
      />
      <InputTextField
        required
        password
        name={t("mypage.profile.confirm_password")}
        text={passwordConfirm}
        handleText={setPasswordConfirm}
        sx={{ mt: "40px", mr: "10px" }}
        placeholder={t("mypage.profile.confirm_password_placeholder")}
        validationMessage={passwordConfirmValidationMessage}
      />
      <Box textAlign="center">
        <Button
          variant="contained"
          className="button_contact_us"
          onClick={() => handleSave()}
          sx={{
            ":hover": {
              bgcolor: "#2DA389",
            },
          }}
        >
          {t("mypage.buttons.save")}
        </Button>
      </Box>
    </Box>
  )
}
